import getContactList, {
  type BeneficiaryContactType,
} from 'api/v2/transactions/getContactList';
import BankIcon from 'assets/svg/icons/bankIcon';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import { Toggle } from 'components/atoms/Form/Toggle';
import { Modal } from 'components/atoms/Modal';
import { RadioGroup } from 'components/atoms/RadioGroups';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import ModalHeading from 'components/molecules/ModalHeading';
import { joiResolver } from 'customJoi';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import Joi from 'joi';
import { motion } from 'motion/react';
import { useRef, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { BankAccountTypes } from 'types/banks.types';
import type { color } from 'types/shared/worlds';
import type { Connector } from 'types/transactions.types';
import { StandingOrderFrequency } from 'types/transactions.types';
import { AvailableCountries } from 'types/users.types';
import {
  fadeTransition,
  structuredCommunicationPatternWithPlus,
} from 'utils/constants';
import { isTaxOfficeName } from 'utils/helpers';
import { formatErrorMessage } from 'validation/commonValidators';
import { IBANvalidationSchema } from '../../../Onboarding/helpers';
import {
  CommunicationTypes,
  isTaxOfficeTransfer,
  isTaxReserveAccountableBank,
  TransferFieldNames,
} from '../../helpers';
import TransferScheduleForm from '../../TransferScheduleForm.component';
import ContactOption from './ContactOption';

export interface InitialPaymentData {
  amount?: number;
  receiptAccount?: Connector | BankAccountTypes.external;
  sourceAccount?: string;
  recipient_iban?: string;
  recipient_name?: string;
  communication?: string;
  communication_type?: CommunicationTypes;
  instant?: boolean;
  period?: StandingOrderFrequency;
  firstExecutionDate?: Date;
  lastExecutionDate?: Date;
}

export interface ExternalTransferProps {
  initialData?: InitialPaymentData;
  onSubmit?: (values: Record<string, any>) => void;
  color?: color;
  loading?: boolean;
  formTitle?: string;
  formSubtitle?: string;
  formSubtitleClassName?: string;
  btnLabel?: string;
  buttonAmplitudeEvent?: string;
  isStandingOrderPayment?: boolean;
  sourceAccountBalance?: number;
  sourceAccount?: Connector;
  formType?: 'basket' | 'external';
}
const ExternalTransfer: FC<ExternalTransferProps> = ({
  initialData = {},
  onSubmit,
  color = 'bank',
  loading,
  formTitle,
  formSubtitle,
  formSubtitleClassName,
  btnLabel,
  buttonAmplitudeEvent,
  isStandingOrderPayment,
  sourceAccountBalance,
  sourceAccount,
  formType,
}) => {
  const { t } = useTranslation();

  const {
    recipient_name,
    amount = 0,
    recipient_iban,
    communication = '',
    instant,
    communication_type,
    period,
    firstExecutionDate,
    lastExecutionDate,
  } = initialData;

  const { data: user } = useGetCurrentCustomerData();

  const IBANplaceholder = useFeatures('settings.IBANPlaceholder');

  const fromTaxReserveAccountableBank =
    isTaxReserveAccountableBank(sourceAccount);

  const externalTransferFormAPI = useForm({
    defaultValues: {
      ...(isStandingOrderPayment
        ? {
            [TransferFieldNames.period]: period || 'Monthly',
            [TransferFieldNames.firstExecutionDate]:
              firstExecutionDate || dayjs().add(1, 'hour').toDate(),
            [TransferFieldNames.lastExecutionDate]: lastExecutionDate,
          }
        : {
            [TransferFieldNames.period]:
              period || StandingOrderFrequency.OneTime,
            [TransferFieldNames.firstExecutionDate]:
              firstExecutionDate || dayjs().toDate(),
            [TransferFieldNames.lastExecutionDate]: lastExecutionDate,
          }),
      [TransferFieldNames.recipient_name]: recipient_name,
      [TransferFieldNames.amount]: amount,
      [TransferFieldNames.recipient_iban]: recipient_iban,
      [TransferFieldNames.communication]: communication,
      [TransferFieldNames.communication_type]:
        user?.country === AvailableCountries.germany
          ? CommunicationTypes.unstructured
          : communication_type || CommunicationTypes.structured,
      [TransferFieldNames.instant]: instant,
    },
    resolver: joiResolver(
      Joi.object({
        [TransferFieldNames.recipient_name]: Joi.string()
          .required()
          .messages(
            formatErrorMessage(
              'accountable_banking',
              TransferFieldNames.recipient_name,
            ),
          ),
        [TransferFieldNames.recipient_iban]: IBANvalidationSchema,
        [TransferFieldNames.amount]: Joi.number()
          .min(1)
          .custom((value, helpers) => {
            if (sourceAccountBalance != null && value > sourceAccountBalance) {
              return helpers.error('number.max');
            }
            return value;
          })
          .required()
          .messages(
            formatErrorMessage(
              'accountable_banking',
              TransferFieldNames.amount,
            ),
          ),
        [TransferFieldNames.communication]: Joi.when(
          TransferFieldNames.communication_type,
          {
            is: CommunicationTypes.structured,
            then: Joi.string().pattern(structuredCommunicationPatternWithPlus),
          },
        )
          .allow('', null)
          .messages(
            formatErrorMessage(
              'accountable_banking',
              TransferFieldNames.communication,
            ),
          ),
        [TransferFieldNames.firstExecutionDate]: Joi.date().required(),
        [TransferFieldNames.period]: Joi.string().required(),
        [TransferFieldNames.lastExecutionDate]: Joi.optional(),
      }),
      { allowUnknown: true, abortEarly: false },
    ),
  });

  const contactRef = useRef(null);

  const [
    communicationTypeValue,
    instantTransferValue,
    recipientNameValue,
    recipientIBANValue,
    periodValue,
    firstExecutionDateValue,
    lastExecutionDateValue,
  ] = externalTransferFormAPI.watch([
    TransferFieldNames.communication_type,
    TransferFieldNames.instant,
    TransferFieldNames.recipient_name,
    TransferFieldNames.recipient_iban,
    TransferFieldNames.period,
    TransferFieldNames.firstExecutionDate,
    TransferFieldNames.lastExecutionDate,
  ]);

  const firstExecutionDateIsToday = dayjs(firstExecutionDateValue).isSame(
    dayjs(),
    'day',
  );

  return (
    <motion.div
      {...fadeTransition}
      key="1"
      className="flex items-center flex-col"
    >
      <ModalHeading
        Icon={BankIcon}
        title={formTitle || t('accountable_banking.external_transfer.title')}
        subtitle={
          formSubtitle || t('accountable_banking.external_transfer.subtitle')
        }
        subtitleClassName={formSubtitleClassName}
      />

      <FormProvider {...externalTransferFormAPI}>
        <form
          className="w-full"
          onSubmit={externalTransferFormAPI.handleSubmit((values) => {
            onSubmit?.(values);
          })}
        >
          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.recipient_name}
            label={t(
              'accountable_banking.external_transfer.fields.recipient_name.label',
            )}
          >
            {({ field }) => (
              <Select
                {...field}
                value={field.value}
                asyncSearch
                name={TransferFieldNames.recipient_name}
                cacheKey={'ContactList'}
                mode="asyncOptionsPaginated"
                placeholder=""
                creatable
                components={{ Option: ContactOption }}
                onChange={(option) => {
                  if (option?.iban) {
                    externalTransferFormAPI.setValue(
                      TransferFieldNames.recipient_iban,
                      option.iban,
                    );
                  }

                  contactRef.current = option;

                  return field.onChange(option?.value);
                }}
                fetchOnEmpty={false}
                onFetch={(name) => {
                  if (
                    (contactRef.current as BeneficiaryContactType | null)
                      ?.name === name
                  )
                    return Promise.resolve({ paging: {}, data: [] });

                  return getContactList({ name }).then((result) => ({
                    ...result,
                    data: result.data.map((item) => ({
                      ...item,
                      label: item.name,
                      value: item.name,
                    })),
                  }));
                }}
                themeOptions={{ hideDropdownIndicator: true }}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name={TransferFieldNames.recipient_iban}
            className="mt-3"
            label={t(
              'accountable_banking.external_transfer.fields.recipient_iban.label',
            )}
          >
            {({ field }) => (
              <div>
                <Select
                  {...field}
                  value={field.value}
                  asyncSearch
                  name={TransferFieldNames.recipient_iban}
                  cacheKey={'ContactList'}
                  placeholder={IBANplaceholder}
                  mode="asyncOptionsPaginated"
                  creatable
                  components={{ Option: ContactOption }}
                  onChange={(option) => {
                    if (option?.name) {
                      externalTransferFormAPI.setValue(
                        TransferFieldNames.recipient_name,
                        option.name,
                      );
                    }

                    contactRef.current = option;

                    return field.onChange(option?.value);
                  }}
                  fetchOnEmpty={false}
                  onFetch={(iban) => {
                    if (
                      (contactRef.current as BeneficiaryContactType | null)
                        ?.iban === iban
                    )
                      return Promise.resolve({ paging: {}, data: [] });

                    return getContactList({ iban: iban }).then((result) => ({
                      ...result,
                      data: result.data.map((item) => ({
                        ...item,
                        label: item.iban,
                        value: item.iban,
                      })),
                    }));
                  }}
                  themeOptions={{ hideDropdownIndicator: true }}
                />

                {isTaxOfficeTransfer(recipientNameValue, recipientIBANValue) ? (
                  <Alert
                    className="mt-2"
                    type="info"
                    title={t(
                      'accountable_banking.external_transfer.tax_office_alert.confirmed',
                    )}
                  />
                ) : isTaxOfficeName(recipientNameValue) &&
                  recipientIBANValue ? (
                  <Alert
                    className="mt-2"
                    type="warning"
                    title={t(
                      'accountable_banking.external_transfer.tax_office_alert.unconfirmed',
                    )}
                  />
                ) : null}
              </div>
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.amount}
            label={t('amountSign')}
          >
            {({ field }) => <PaymentAmount {...field} />}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.communication_type}
            label={t(
              'accountable_banking.external_transfer.fields.communication.label',
            )}
            showUI={user?.country === AvailableCountries.belgium}
          >
            {({ field }) => (
              <RadioGroup
                {...field}
                type="inline"
                labelClassNames="text-black text-sm"
                data={[
                  {
                    title: t(
                      'accountable_banking.external_transfer.fields.communication.structured',
                    ),
                    id: 'structured',
                    checked: communicationTypeValue === 'structured',
                  },
                  {
                    title: t(
                      'accountable_banking.external_transfer.fields.communication.unstructured',
                    ),
                    id: 'unstructured',
                    checked: communicationTypeValue === 'unstructured',
                  },
                ]}
              />
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.communication}
            label={
              user?.country === AvailableCountries.germany
                ? t(
                    'accountable_banking.external_transfer.fields.communication.label',
                  )
                : undefined
            }
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                key={communicationTypeValue}
                type={
                  communicationTypeValue === CommunicationTypes.structured
                    ? 'maskedInput'
                    : 'text'
                }
                placeholder={
                  communicationTypeValue === CommunicationTypes.structured
                    ? '++++XXX/XXXX/XXXX+++'
                    : ''
                }
                maskProps={
                  communicationTypeValue === CommunicationTypes.structured
                    ? {
                        mask: '+++999/9999/99999+++',
                        maskChar: '',
                        alwaysShowMask: false,
                        formatChars: {
                          '9': '[0-9]',
                          a: '[A-Za-z]',
                          '*': '[A-Za-z0-9]',
                          S: '[A-Za-z0-9]',
                        },
                      }
                    : undefined
                }
                onChange={({ target: { value } }) => {
                  const _value = value || '';
                  field.onChange(_value);
                }}
              />
            )}
          </FieldConnector>

          {formType !== 'basket' && !fromTaxReserveAccountableBank ? (
            <Modal
              name="standing-order-fields"
              trigger={({ openModal }) => (
                <div className="my-3">
                  <label
                    htmlFor="standing-order-fields"
                    className="first-letter:capitalize font-semibold text-sm text-primary-800"
                  >
                    {t(
                      'accountable_banking.scheduled_payments.schedule_form.when',
                    )}
                  </label>

                  <Select
                    name="ui"
                    value={`${t(periodValue)}, ${
                      firstExecutionDateIsToday
                        ? t('today')
                        : dayjs(firstExecutionDateValue).format('DD MMMM YYYY')
                    }`}
                    openMenuOnClick
                    isSearchable={false}
                    noOptionsMessage={() => null}
                    placeholder=""
                    onMenuOpen={() => {
                      openModal();
                    }}
                    themeOptions={{ hideDropdownIndicator: true }}
                  />
                </div>
              )}
              render={({ closeModal }) => (
                <TransferScheduleForm
                  sourceAccount={sourceAccount}
                  defaultValues={{
                    [TransferFieldNames.period]: periodValue,
                    [TransferFieldNames.firstExecutionDate]:
                      firstExecutionDateValue,
                    [TransferFieldNames.lastExecutionDate]:
                      lastExecutionDateValue,
                  }}
                  onSubmit={(schedule) => {
                    externalTransferFormAPI.setValue(
                      TransferFieldNames.period,
                      schedule[TransferFieldNames.period],
                    );

                    externalTransferFormAPI.setValue(
                      TransferFieldNames.firstExecutionDate,
                      schedule[TransferFieldNames.firstExecutionDate],
                    );

                    externalTransferFormAPI.setValue(
                      TransferFieldNames.lastExecutionDate,
                      schedule[TransferFieldNames.lastExecutionDate],
                    );

                    closeModal();
                  }}
                />
              )}
            />
          ) : null}

          <div className="flex items-center mt-3 space-x-4">
            <Toggle
              color={color}
              enabled={instantTransferValue}
              onChange={(value) => {
                externalTransferFormAPI.setValue(
                  TransferFieldNames.instant,
                  value,
                );
              }}
            />

            <span className="text-primary-700">
              {t(
                'accountable_banking.external_transfer.fields.internal_transfer.label',
              )}
            </span>
          </div>

          <Button
            className="w-full mt-12"
            type="submit"
            loading={loading}
            tracingEvent={buttonAmplitudeEvent}
          >
            {btnLabel || t('continue')}
          </Button>
        </form>
      </FormProvider>
    </motion.div>
  );
};

export default ExternalTransfer;
